import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Space } from '@client/components/_ui/layout/Space';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import type { RouterOutputs } from '@client/trpc/client';
import { hasBlockingQueryError } from '@client/trpc/error-classification';
import { formatDateTime } from '@shared/utils/core/formatting';
import { createFileRoute } from '@tanstack/react-router';
import { Descriptions, Select, Spin, Table, Tag } from 'antd';
import { ArrowLeft, GitCompareArrows } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';

const searchSchema = z.object({
    runA: z.string().optional(),
    runB: z.string().optional(),
});

export const Route = createFileRoute('/admin-console/ai/eval-runs/compare')({
    validateSearch: searchSchema,
    staticData: {
        permissions: ['admin_ai:read'],
        presentation: 'fullPage' as const,
        breadcrumb: {
            title: 'Compare runs',
        },
        ui: {
            icon: GitCompareArrows,
            title: 'Compare eval runs',
            subtitle: 'Side-by-side comparison of two evaluation runs',
        },
    },
    component: CompareEvalRuns,
});

const resultStatusTagColor: Record<string, string> = {
    PASSED: 'green',
    FAILED: 'red',
    ERROR: 'orange',
    PENDING: 'default',
};

const resultStatusLabel: Record<string, string> = {
    PASSED: 'Passed',
    FAILED: 'Failed',
    ERROR: 'Error',
    PENDING: 'Pending',
};

type ComparisonResult = RouterOutputs['aiEval']['compareEvalRuns']['comparison']['matchedResults'][number];

function formatDuration(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
}

function CompareEvalRuns() {
    const navigate = useNavigate();
    const { trpc } = useTrpc();
    const { runA: runAParam, runB: runBParam } = Route.useSearch();

    const [selectedRunA, setSelectedRunA] = useState<string | undefined>(runAParam);
    const [selectedRunB, setSelectedRunB] = useState<string | undefined>(runBParam);

    // Fetch available runs for the select dropdowns
    const { data: runs } = trpc.aiEval.listEvalRuns.useQuery({ limit: 100 });

    const runOptions =
        runs?.rows.map((r) => ({
            label: `#${r.id} — ${r.promptKey} v${r.promptVersion} (${r.status})`,
            value: String(r.id),
        })) ?? [];

    const canCompare = selectedRunA && selectedRunB && selectedRunA !== selectedRunB;

    const {
        data: comparison,
        isLoading,
        isFetching,
        error,
        refetch,
    } = trpc.aiEval.compareEvalRuns.useQuery(
        { runIdA: BigInt(selectedRunA ?? '0'), runIdB: BigInt(selectedRunB ?? '0') },
        { enabled: !!canCompare },
    );
    const hasBlockingError = hasBlockingQueryError(error, comparison);

    const columns = [
        {
            title: 'Test case',
            dataIndex: 'testCaseName',
            key: 'testCaseName',
        },
        {
            title: 'Run A',
            key: 'statusA',
            width: 100,
            render: (_: unknown, record: ComparisonResult) => {
                if (!record.resultA) return <Tag variant="outlined">N/A</Tag>;
                const status = record.resultA.status as string;
                return (
                    <Tag color={resultStatusTagColor[status] ?? 'default'} variant="outlined">
                        {resultStatusLabel[status] ?? status}
                    </Tag>
                );
            },
        },
        {
            title: 'Run B',
            key: 'statusB',
            width: 100,
            render: (_: unknown, record: ComparisonResult) => {
                if (!record.resultB) return <Tag variant="outlined">N/A</Tag>;
                const status = record.resultB.status as string;
                return (
                    <Tag color={resultStatusTagColor[status] ?? 'default'} variant="outlined">
                        {resultStatusLabel[status] ?? status}
                    </Tag>
                );
            },
        },
        {
            title: 'Change',
            key: 'change',
            width: 120,
            render: (_: unknown, record: ComparisonResult) => {
                if (!record.resultA)
                    return (
                        <Tag color="blue" variant="outlined">
                            New in B
                        </Tag>
                    );
                if (!record.resultB)
                    return (
                        <Tag color="orange" variant="outlined">
                            Only in A
                        </Tag>
                    );
                if (!record.statusChanged) return <Tag variant="outlined">Unchanged</Tag>;

                if (record.resultB.status === 'PASSED' && record.resultA.status !== 'PASSED') {
                    return (
                        <Tag color="green" variant="outlined">
                            Improved
                        </Tag>
                    );
                }
                if (record.resultA.status === 'PASSED' && record.resultB.status !== 'PASSED') {
                    return (
                        <Tag color="red" variant="outlined">
                            Regression
                        </Tag>
                    );
                }
                return (
                    <Tag color="orange" variant="outlined">
                        Changed
                    </Tag>
                );
            },
        },
        {
            title: 'Token delta',
            key: 'tokenDelta',
            width: 100,
            render: (_: unknown, record: ComparisonResult) => {
                const total = record.tokenDelta.prompt + record.tokenDelta.completion;
                if (total === 0) return '-';
                return (
                    <span className={total > 0 ? 'text-orange-500' : 'text-green-600'}>
                        {total > 0 ? '+' : ''}
                        {total.toLocaleString()}
                    </span>
                );
            },
        },
        {
            title: 'Time delta',
            key: 'timeDelta',
            width: 100,
            render: (_: unknown, record: ComparisonResult) => {
                if (record.timeDelta === 0) return '-';
                return (
                    <span className={record.timeDelta > 0 ? 'text-orange-500' : 'text-green-600'}>
                        {record.timeDelta > 0 ? '+' : ''}
                        {formatDuration(Math.abs(record.timeDelta))}
                    </span>
                );
            },
        },
    ];

    const getRowClassName = (record: ComparisonResult) => {
        if (!record.resultA || !record.resultB || !record.statusChanged) return '';
        if (record.resultB.status === 'PASSED' && record.resultA.status !== 'PASSED')
            return 'bg-green-50 dark:bg-green-950/30';
        if (record.resultA.status === 'PASSED' && record.resultB.status !== 'PASSED')
            return 'bg-red-50 dark:bg-red-950/30';
        return '';
    };

    return (
        <PageController route={Route}>
            <div className="space-y-6">
                {/* Back button and selectors */}
                <div className="flex items-center gap-4">
                    <Button
                        type="tertiary"
                        icon={<ArrowLeft />}
                        onClick={() => void navigate({ to: '/admin-console/ai/eval-runs' })}
                    >
                        Back
                    </Button>
                </div>

                <div className="flex items-end gap-4">
                    <div className="flex-1">
                        <div className="mb-1 font-medium text-sm">Run A (baseline)</div>
                        <Select
                            className="w-full"
                            value={selectedRunA}
                            onChange={setSelectedRunA}
                            options={runOptions}
                            showSearch
                            placeholder="Select run A"
                            filterOption={(input, option) =>
                                (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
                            }
                        />
                    </div>
                    <GitCompareArrows className="mb-2 text-muted-foreground" />
                    <div className="flex-1">
                        <div className="mb-1 font-medium text-sm">Run B (comparison)</div>
                        <Select
                            className="w-full"
                            value={selectedRunB}
                            onChange={setSelectedRunB}
                            options={runOptions}
                            showSearch
                            placeholder="Select run B"
                            filterOption={(input, option) =>
                                (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
                            }
                        />
                    </div>
                </div>

                {hasBlockingError ? (
                    <InlineQueryError
                        error={error}
                        onRetry={() => void refetch()}
                        retrying={isFetching}
                        title="Couldn't load comparison"
                    />
                ) : (
                    <>
                        {error && (
                            <InlineQueryError
                                error={error}
                                onRetry={() => void refetch()}
                                retrying={isFetching}
                                title="Couldn't refresh comparison"
                                compact
                            />
                        )}

                        {isLoading && (
                            <div className="flex justify-center py-12">
                                <Spin />
                            </div>
                        )}

                        {comparison && (
                            <>
                                {/* Summary */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="rounded-lg border border-separator bg-card p-4">
                                        <div className="mb-2 font-medium text-sm">Run A</div>
                                        <Descriptions size="small" column={1}>
                                            <Descriptions.Item label="Prompt">
                                                {comparison.runA.promptKey} v{comparison.runA.promptVersion}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Model">
                                                {comparison.runA.provider} / {comparison.runA.model}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Pass rate">
                                                {comparison.runA.passedTestCases}/{comparison.runA.totalTestCases}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Created">
                                                {formatDateTime(
                                                    new Date(comparison.runA.createdAt),
                                                    'dd/MM/yyyy HH:mm',
                                                )}
                                            </Descriptions.Item>
                                        </Descriptions>
                                    </div>
                                    <div className="rounded-lg border border-separator bg-card p-4">
                                        <div className="mb-2 font-medium text-sm">Run B</div>
                                        <Descriptions size="small" column={1}>
                                            <Descriptions.Item label="Prompt">
                                                {comparison.runB.promptKey} v{comparison.runB.promptVersion}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Model">
                                                {comparison.runB.provider} / {comparison.runB.model}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Pass rate">
                                                {comparison.runB.passedTestCases}/{comparison.runB.totalTestCases}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Created">
                                                {formatDateTime(
                                                    new Date(comparison.runB.createdAt),
                                                    'dd/MM/yyyy HH:mm',
                                                )}
                                            </Descriptions.Item>
                                        </Descriptions>
                                    </div>
                                </div>

                                {/* Delta summary */}
                                <Space size="small">
                                    {comparison.comparison.summary.newPasses > 0 && (
                                        <Tag color="green" variant="outlined">
                                            {comparison.comparison.summary.newPasses} new passes
                                        </Tag>
                                    )}
                                    {comparison.comparison.summary.newFailures > 0 && (
                                        <Tag color="red" variant="outlined">
                                            {comparison.comparison.summary.newFailures} new failures
                                        </Tag>
                                    )}
                                    <Tag variant="outlined">{comparison.comparison.summary.unchanged} unchanged</Tag>
                                    {comparison.comparison.summary.onlyInA > 0 && (
                                        <Tag color="orange" variant="outlined">
                                            {comparison.comparison.summary.onlyInA} only in A
                                        </Tag>
                                    )}
                                    {comparison.comparison.summary.onlyInB > 0 && (
                                        <Tag color="blue" variant="outlined">
                                            {comparison.comparison.summary.onlyInB} only in B
                                        </Tag>
                                    )}
                                </Space>

                                {/* Per-test-case comparison */}
                                <Table
                                    dataSource={comparison.comparison.matchedResults}
                                    columns={columns}
                                    rowKey="testCaseName"
                                    size="small"
                                    pagination={false}
                                    rowClassName={getRowClassName}
                                />
                            </>
                        )}
                    </>
                )}
            </div>
        </PageController>
    );
}
